import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { OfferCardBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";

type Props = Immutable<{
    value: Value;
    /**
     * 1-indexed position within the parent ``OfferSectionBlock`` list.
     * When set, appends ``?pid=learnmore{cardNum}[_{refPage}]`` to the
     * learn-more anchor for tracking, matching the legacy Django template
     * at ``cms/components/offer-card-block.html``.
     */
    cardNum?: number;
    /** Tracking suffix appended to ``pid`` query string (e.g. "shopallpillows"). */
    refPage?: string;
}>;

/**
 * React port of the pillows-specific ``OfferCardBlock`` (defined in
 * ``server/cms/components/ads.py``). Mirrors the original Django template
 * 1:1 — including the ``|safe`` CharBlock renders for ``lead_in``,
 * ``title1``, ``title2``, ``sub_title`` (content authors put inline HTML
 * in those fields, so they map to dangerouslySetInnerHTML; trust boundary
 * is the CMS, identical to the existing Django `|safe` usage).
 */
export const OfferCardBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const supportIconUrl = v.support_icon?.url;
    // Match the legacy template's `{% image value.support_image max-200x120 format-png %}`
    // — the rendition is server-declared via extra_client_data_renditions on the block.
    const supportImageRendition =
        v.support_image?.renditions["max-200x120|format-png"];
    const supportImageUrl = supportImageRendition?.url ?? v.support_image?.url;
    const learnMoreUrl = v.learn_more?.url ?? "";
    const trackingSuffix = props.cardNum
        ? `?pid=learnmore${props.cardNum}${props.refPage ? `_${encodeURIComponent(props.refPage)}` : ""}`
        : "";
    return (
        <article className="offer-list__card">
            <div className="offer-list__card-copy">
                {supportIconUrl && (
                    <div className="offer-list__card-icon">
                        {/* Decorative: the legacy template rendered a bare
                            <img> with no alt; mark it presentational. */}
                        <img
                            src={supportIconUrl}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                )}
                <h5
                    className="offer-list__card-lead-in"
                    dangerouslySetInnerHTML={{ __html: v.lead_in ?? "" }}
                />
                <h2
                    className="offer-list__card-title offer-list__card-title--1"
                    dangerouslySetInnerHTML={{ __html: v.title1 ?? "" }}
                />
                <h3
                    className="offer-list__card-title offer-list__card-title--2"
                    dangerouslySetInnerHTML={{ __html: v.title2 ?? "" }}
                />
                <h4
                    className="offer-list__card-sub-title"
                    dangerouslySetInnerHTML={{ __html: v.sub_title ?? "" }}
                />
                <RichTextBlock value={v.summary ?? ""} />
            </div>
            <a
                href={`${learnMoreUrl}${trackingSuffix}`}
                title={t`Learn More`}
                className="offer-list__card-cta"
            >
                {t`Learn More`}
            </a>
            {supportImageUrl && (
                <div className="offer-list__card-image">
                    {/* Mirror Wagtail's `{% image %}` tag, which emits an alt
                        derived from the image's default alt text / title. */}
                    <img
                        src={supportImageUrl}
                        alt={
                            v.support_image?.default_alt_text ||
                            v.support_image?.title ||
                            ""
                        }
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            )}
        </article>
    );
};
